import React, { useState, useEffect } from 'react';
import { 
  TableRow, 
  TableCell, 
  Typography, 
  Box, 
  IconButton, 
  Tooltip,
  useTheme,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import octopusInstance from '../../../services/octopus/octopusInstance';
import { getExchangeCode } from '../../../services/exchanges';
import { styles } from './BasketInstrumentRowCSS';

const BasketInstrumentRow = ({ instrument, basketId, onModify, onDelete, isExecuted, onLtpUpdate, isMobile = false, marginsCacheRef }) => {
  const theme = useTheme();
  const [localLtp, setLocalLtp] = useState({
    ltp: 0,
    absoluteChange: 0,
    percentChange: 0
  });
  const [localMargin, setLocalMargin] = useState(() => {
    if (marginsCacheRef && marginsCacheRef.current && marginsCacheRef.current[instrument.id] !== undefined) {
      return marginsCacheRef.current[instrument.id];
    }
    return undefined;
  });

  useEffect(() => {
    const handleMarginUpdate = (e) => {
      if (e.detail && e.detail[instrument.id] !== undefined) {
        setLocalMargin(e.detail[instrument.id]);
      }
    };
    window.addEventListener('BASKET_MARGINS_UPDATED', handleMarginUpdate);
    return () => window.removeEventListener('BASKET_MARGINS_UPDATED', handleMarginUpdate);
  }, [instrument.id]);

  useEffect(() => {
    if (!instrument.instrument_token) return;

    const handler = octopusInstance.wsHandler({
      messageType: "CompactMarketDataMessage",
      subscriptionLocation: `basket-${basketId}-${instrument.id}`,
      payload: {
        exchangeCode: getExchangeCode(instrument.exchange),
        instrumentToken: Number(instrument.instrument_token),
      },
    });

    if (!handler) return;

    handler.subscribe(({ msg }) => {
      setLocalLtp({
        ltp: msg.ltp || 0,
        absoluteChange: msg.absoluteChange || 0,
        percentChange: msg.percentChange || 0
      });
      if (onLtpUpdate) {
        onLtpUpdate(instrument.id, {
          ltp: msg.ltp || 0,
          bestBid: msg.bestBidPrice || msg.bestBid || 0,
          bestAsk: msg.bestAskPrice || msg.bestAsk || 0
        });
      }
    });

    return () => handler.unsubscribe();
  }, [instrument.instrument_token, instrument.exchange, basketId, instrument.id, onLtpUpdate]);

  const renderTag = (text) => {
    if (!text) return null;
    return (
      <Box sx={styles.tagBox(theme)}>
        {text}
      </Box>
    );
  };

  const isBuy = instrument.order_side === 'BUY';

  if (isMobile) {
    return (
      <Box sx={styles.mobileContainer(theme)}>
        <Box sx={styles.mobileRow1}>
          <Box sx={styles.mobileSymbolBox}>
            <Typography variant="body2" fontWeight={600} sx={styles.mobileSymbolText}>
              {instrument.trading_symbol}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={styles.mobileExchangeText}>
              {instrument.exchange}
            </Typography>
            <Box sx={styles.mobileTagsBox}>
              {renderTag(instrument.product === 'MIS' ? 'INTRADAY' : 'OVERNIGHT')}
              {renderTag(instrument.order_type)}
            </Box>
          </Box>
          <Chip
            size="small"
            label={instrument.order_side}
            color={isBuy ? "success" : "error"}
            variant="outlined"
            sx={styles.mobileSideChip}
          />
        </Box>
        
        <Box sx={styles.mobileRowData}>
          <Box sx={styles.mobileDataColLeft}>
             <Typography variant="caption" color="text.secondary" sx={styles.mobileDataLabel}>LTP</Typography>
             <Typography variant="body2" fontWeight={500} sx={styles.mobileDataValue}>
               ₹{localLtp.ltp.toFixed(2)}
             </Typography>
          </Box>
          <Box sx={styles.mobileDataColCenter}>
             <Typography variant="caption" color="text.secondary" sx={styles.mobileDataLabel}>Price</Typography>
             <Typography variant="body2" fontWeight={500} sx={styles.mobileDataValue}>
               ₹{instrument.order_type === 'MARKET' ? '0.00' : Number(instrument.price || 0).toFixed(2)}
             </Typography>
          </Box>
          <Box sx={styles.mobileDataColRight}>
            <IconButton size="small" color="primary" onClick={(e) => onModify(instrument, e)} disabled={isExecuted} sx={styles.mobileActionBtn(theme)}>
              <EditIcon sx={styles.mobileActionIcon} />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={styles.mobileRowData}>
          <Box sx={styles.mobileDataColLeft}>
             <Typography variant="caption" color="text.secondary" sx={styles.mobileDataLabel}>Qty</Typography>
             <Typography variant="body2" fontWeight={500} sx={styles.mobileDataValue}>
               {instrument.quantity}
             </Typography>
          </Box>
          <Box sx={styles.mobileDataColCenter}>
             <Typography variant="caption" color="text.secondary" sx={styles.mobileDataLabel}>Margin</Typography>
             <Typography variant="body2" fontWeight={500} sx={styles.mobileDataValue}>
               {localMargin !== undefined ? `₹${localMargin.toFixed(2)}` : '--'}
             </Typography>
          </Box>
          <Box sx={styles.mobileDataColRight}>
            <IconButton size="small" color="error" onClick={(e) => onDelete(instrument.id, e)} disabled={isExecuted} sx={styles.mobileActionBtn(theme)}>
              <DeleteIcon sx={styles.mobileActionIcon} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <TableRow sx={styles.desktopTableRow(theme)}>
      <TableCell component="th" scope="row">
        <Box sx={styles.desktopSymbolBox}>
          <Typography variant="body2" fontWeight={600}>
            {instrument.trading_symbol}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {instrument.exchange}
          </Typography>
        </Box>
        <Box sx={styles.desktopTagsBox}>
          {renderTag(instrument.product === 'MIS' ? 'INTRADAY' : 'OVERNIGHT')}
          {renderTag(instrument.order_type)}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          ₹{localLtp.ltp.toFixed(2)}
        </Typography>
        <Typography 
          variant="caption" 
          color={localLtp.absoluteChange >= 0 ? 'success.main' : 'error.main'}
        >
          {localLtp.absoluteChange > 0 ? '+' : ''}{localLtp.absoluteChange.toFixed(2)} ({localLtp.percentChange > 0 ? '+' : ''}{localLtp.percentChange.toFixed(2)}%)
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={instrument.order_side}
          color={isBuy ? "success" : "error"}
          variant="outlined"
        />
      </TableCell>
      <TableCell align="center">
        {instrument.quantity}
      </TableCell>
      <TableCell align="center">
        ₹{instrument.order_type === 'MARKET' ? '0.00' : Number(instrument.price || 0).toFixed(2)}
      </TableCell>
      <TableCell align="center">
        {localMargin !== undefined ? `₹${localMargin.toFixed(2)}` : '--'}
      </TableCell>
      <TableCell align="center">
        <Box sx={styles.desktopActionsBox}>
          <Tooltip title="Modify Order">
            <span>
              <IconButton size="small" color="primary" onClick={(e) => onModify(instrument, e)} disabled={isExecuted}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete Order">
            <span>
              <IconButton size="small" color="error" onClick={(e) => onDelete(instrument.id, e)} disabled={isExecuted}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default React.memo(BasketInstrumentRow);
